import type { GoalView, WeekDay } from '@aj/core';
import { Card, HStack, LiquidDay, Pressable, ProgressBar, Text, VStack, cx } from '@aj/ui';
import { useState } from 'react';

import { dateToMs } from '../../lib/dates.js';
import { useI18n, useT } from '../../providers/I18nProvider.js';

export type WeekCardProps = {
  week: WeekDay[];
  goal: GoalView | null;
};

/**
 * One card, two faces: this week's consistency, and the goal it is for.
 *
 * The flip is manual and remembered only for the session. Nothing here
 * auto-rotates — a card that changes what it says while you are reading it is
 * a card you stop reading.
 */
export function WeekCard({ week, goal }: WeekCardProps) {
  const t = useT();
  const [face, setFace] = useState<'week' | 'goal'>('week');
  const flippable = goal !== null;
  const showing = flippable ? face : 'week';

  const body =
    showing === 'goal' && goal ? <GoalFace goal={goal} /> : <WeekFace week={week} />;

  if (!flippable) return <Card padding={4}>{body}</Card>;

  return (
    <Card padding={0}>
      <Pressable
        aria-label={t('nav.jatra')}
        onClick={() => setFace(showing === 'week' ? 'goal' : 'week')}
        className="flex w-full flex-col gap-3 p-4 text-left"
      >
        {body}
        <HStack gap={0} justify="center" align="center" className="gap-1.5">
          {(['week', 'goal'] as const).map((id) => (
            <span
              key={id}
              aria-hidden
              className={cx(
                'size-1.5 rounded-full transition-colors',
                id === showing ? 'bg-accent' : 'bg-line-strong',
              )}
            />
          ))}
        </HStack>
      </Pressable>
    </Card>
  );
}

function WeekFace({ week }: { week: WeekDay[] }) {
  const t = useT();
  const i18n = useI18n();

  return (
    <HStack gap={1} justify="between" align="center">
      {week.map((day) => {
        const weekday = i18n.formatDate(dateToMs(day.date), { weekday: 'narrow' });
        return (
          <VStack key={day.date} gap={2} align="center" className="flex-1">
            <Text variant="caption" tone={day.isToday ? 'accent' : 'subtle'}>
              {weekday}
            </Text>
            <LiquidDay
              fill={day.ratio}
              variant={day.state === 'future' ? 'future' : day.isToday ? 'today' : 'past'}
              label={
                day.scheduled === 0
                  ? t('week.dayEmpty', { day: weekday })
                  : t('week.dayLabel', { day: weekday, done: day.done, total: day.scheduled })
              }
            />
          </VStack>
        );
      })}
    </HStack>
  );
}

/**
 * The destination, on Home.
 *
 * A bar appears only when the goal has parts to count. A goal like "get fit"
 * has no honest denominator, so it shows where it is going and when — never
 * an invented percentage, and never a countdown of days elapsed, which would
 * fill up on its own whether or not the student did anything.
 */
function GoalFace({ goal }: { goal: GoalView }) {
  const t = useT();

  return (
    <VStack gap={2}>
      <HStack gap={3} justify="between" align="baseline">
        <Text variant="label" className="min-w-0 truncate">
          {goal.title}
        </Text>
        {goal.progress !== null && (
          <Text variant="label" tone="accent" numeric>
            {t('goal.partsOf', { done: goal.partsDone, total: goal.partsTotal })}
          </Text>
        )}
      </HStack>
      {goal.progress !== null && (
        <ProgressBar value={goal.progress} size="sm" label={goal.title} />
      )}
      <Text variant="caption" tone="subtle">
        {t('home.daysLeft', { count: goal.daysLeft })}
      </Text>
    </VStack>
  );
}
